import { DEFAULT_INITIAL_SEARCH_DATA } from "@saleor/config";
import {
  useAssignCouponToCollection,
  useCouponCreate,
  useDraftCouponCreate
} from "@saleor/coupons/mutations";
import { CouponCreate as CouponCreateData } from "@saleor/coupons/types/CouponCreate";
import { DraftCouponCreate } from "@saleor/coupons/types/DraftCouponCreate";
import { couponManageUrl } from "@saleor/coupons/url";
import {
  canManageCouponUsageLimitByDealType,
  resolveCouponUsageLimitForCreate
} from "@saleor/coupons/utils";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import useUser from "@saleor/hooks/useUser";
import { findValueInEnum, maybe } from "@saleor/misc";
import useCouponCollectionSearch from "@saleor/searches/useCouponCollectionSearch";
import useMallSearch from "@saleor/searches/useMallSearch";
import {
  CouponAssigneeEnum,
  CouponCategoryEnum,
  CouponCollectionSortField,
  CouponCollectionStatusEnum,
  CouponDealTypeEnum,
  CouponOccurenceEnum,
  CouponOccurenceExcludeEnum,
  CouponRedeemTypeEnum,
  CouponStatusEnum,
  CouponUserTypeEnum,
  MallSortField,
  OrderBehavior,
  OrderDirection,
  SmopStatusEnum
} from "@saleor/types/globalTypes";
import { changeTimeZone } from "@saleor/utils/smop/changeTimeZoneDateTime";
import { formDataValidations } from "@saleor/utils/validators/coupon";
import React, { useState } from "react";
import { useIntl } from "react-intl";

import CouponCreatePage, {
  CouponCreateFormData
} from "../../components/CouponCreatePage";

export const CouponCreateView: React.FC = () => {
  const notify = useNotifier();
  const intl = useIntl();
  const { user } = useUser();

  const userPermissions = user.userPermissions;
  const [selectedCategory, setSelectedCategory] = useState([]);
  const [selectedCollection, setSelectedCollection] = useState("");

  const {
    loadMore: loadMoreMalls,
    search: searchMall,
    result: searchMallOpts
  } = useMallSearch({
    variables: {
      ...DEFAULT_INITIAL_SEARCH_DATA,
      filterStatus: SmopStatusEnum.ACT,
      first: 20,
      sortBy: {
        behavior: OrderBehavior.CASE_INSENSITIVE,
        direction: OrderDirection.ASC,
        field: MallSortField.NAME
      },
      userMalls: true
    }
  });
  const mallTags = maybe(() => searchMallOpts.data.search.edges, []).map(
    edge => edge.node
  );

  const {
    loadMore: loadMoreCollection,
    search: searchCollection,
    result: searchCollectionOpts
  } = useCouponCollectionSearch({
    variables: {
      ...DEFAULT_INITIAL_SEARCH_DATA,
      category: selectedCategory,
      sortBy: {
        direction: OrderDirection.DESC,
        field: CouponCollectionSortField.UPDATED_AT
      },
      status: [
        CouponCollectionStatusEnum.ACTIVE,
        CouponCollectionStatusEnum.INACTIVE,
        CouponCollectionStatusEnum.SCHEDULED,
        CouponCollectionStatusEnum.EXPIRED
      ]
    }
  });

  const [promoImageUpload, setPromoImageUpload] = React.useState(null);
  const [accessImageUpload, setAccessImageUpload] = React.useState(null);
  const navigate = useNavigator();

  const [assignCouponToCollection] = useAssignCouponToCollection({
    onCompleted: data => {
      if (data.setCouponsToCollection.errors.length !== 0) {
        notify({
          status: "error",
          text: data.setCouponsToCollection.errors[0].message
        });
      }
    }
  });

  const handleSetCouponToCollection = async (couponId: string) => {
    await assignCouponToCollection({
      variables: {
        couponId,
        instanceId: selectedCollection
      }
    });
  };

  const handleSuccess = (data: CouponCreateData) => {
    if (data?.couponCreate?.errors.length === 0) {
      notify({
        status: "success",
        text: intl.formatMessage({
          defaultMessage: "Coupon Created Successfully!"
        })
      });
      if (selectedCollection) {
        handleSetCouponToCollection(data?.couponCreate?.coupon?.id);
      }
      navigate(couponManageUrl);
    } else if (data.couponCreate.errors.some(e => e.code === "REQUIRED")) {
      notify({
        status: "error",
        text: intl.formatMessage({
          defaultMessage: "Some required fields are empty"
        })
      });
    } else if (data.couponCreate.errors.some(e => e.code === "INVALID")) {
      notify({
        status: "error",
        text: data.couponCreate.errors[0].message,
        title: "Coupon Form Error"
      });
    } else {
      notify({
        status: "error",
        text: intl.formatMessage({
          defaultMessage: "Error"
        })
      });
    }
  };

  const handleDraftSuccess = (data: DraftCouponCreate) => {
    if (data?.draftCouponCreate.errors.length === 0) {
      notify({
        status: "success",
        text: intl.formatMessage({
          defaultMessage: "Coupon Saved as Draft"
        })
      });
      if (selectedCollection) {
        handleSetCouponToCollection(data?.draftCouponCreate?.coupon?.id);
      }

      navigate(couponManageUrl);
    } else if (data?.draftCouponCreate.errors.some(e => e.code === "INVALID")) {
      notify({
        status: "error",
        text: data.draftCouponCreate.errors[0].message
      });
    } else {
      notify({
        status: "error",
        text: intl.formatMessage({
          defaultMessage: "Error"
        })
      });
    }
  };

  const [couponCreate, couponCreateResult] = useCouponCreate({
    onCompleted: handleSuccess
  });

  const [couponDraftCreate, couponDraftCreateResult] = useDraftCouponCreate({
    onCompleted: handleDraftSuccess
  });

  const onSubmit = async (formData: CouponCreateFormData) => {
    setSelectedCollection(formData.couponCollection);
    const result = await couponCreate({
      variables: {
        first: 100,
        input: {
          accessImage: accessImageUpload,
          accessPageDescription: JSON.stringify(formData.accessPageDescription),
          accessPageSubtitle: formData.subtitle,
          accessPageTagline: formData.tagline,
          assignee: maybe(() =>
            findValueInEnum(formData.assignee, CouponAssigneeEnum)
          ),
          category: maybe(() =>
            findValueInEnum(formData.category, CouponCategoryEnum)
          ),
          code: formData.couponCode,
          dealType: maybe(() =>
            findValueInEnum(formData.couponClassification, CouponDealTypeEnum)
          ),
          discountValue: formData.couponValue,
          endDate: changeTimeZone(
            formData.couponEndDate,
            formData.couponEndTime
          ),
          eventEndDate: changeTimeZone(
            formData.eventStartDate,
            formData.eventStartTime
          ),
          eventLocation: formData.eventLocation,
          eventStartDate: changeTimeZone(
            formData.eventStartDate,
            formData.eventStartTime
          ),
          hasMalls: formData.hasMalls,
          isAppExclusive: formData.isAppExclusive,
          isExclusiveRedemption: formData.isExclusiveRedemption,
          isFeatured: formData.isFeatured,
          isHidden: formData.isHidden,
          isNationwide: formData.isNationwide,
          isUsageTimerApplied: formData.isUsageTimerApplied,
          mainPromoDescription: JSON.stringify(formData.mainPromoDescription),
          mainPromoTitle: formData.mainPromoTitle,
          malls: formData.malls,
          mechanics: JSON.stringify(formData.mechanics),
          name: formData.mainPromoTitle,
          occurence: maybe(() =>
            findValueInEnum(formData.occurrence, CouponOccurenceEnum)
          ),
          occurenceExclude: maybe(() => {
            const excludedDaysArray = Array.isArray(formData.excludedDays)
              ? formData.excludedDays
              : [formData.excludedDays];
            return excludedDaysArray.map(day =>
              findValueInEnum(day, CouponOccurenceExcludeEnum)
            );
          }),
          promoImage: promoImageUpload,
          quantity: formData.totalCopies ? Number(formData.totalCopies) : null,
          redeemEndDate: changeTimeZone(
            formData.redeemingEndDate,
            formData.redeemingEndTime
          ),
          redeemStartDate: changeTimeZone(
            formData.redeemingStartDate,
            formData.redeemingStartTime
          ),
          redeemType: maybe(() =>
            findValueInEnum(formData.redeemType, CouponRedeemTypeEnum)
          ),
          redemptionExclude: maybe(() => {
            const excludedDaysArray = Array.isArray(formData.excludedDays)
              ? formData.excludedDays
              : [formData.excludedDays];
            return excludedDaysArray.map(day =>
              findValueInEnum(day, CouponOccurenceExcludeEnum)
            );
          }),
          redemptionFrequency: maybe(() =>
            findValueInEnum(formData.occurrence, CouponOccurenceEnum)
          ),
          startDate: changeTimeZone(
            formData.couponStartDate,
            formData.couponStartTime
          ),
          status: CouponStatusEnum.ACTIVE,
          usageLimit: resolveCouponUsageLimitForCreate(
            canManageCouponUsageLimitByDealType(
              userPermissions,
              formData.couponClassification
            ),
            formData.usageLimit
          ),
          usageTimerHour: formData.usageTimerHour,
          usageTimerMinute: formData.usageTimerMinute,
          usageTimerSecond: formData.usageTimerSecond,
          userType: maybe(() =>
            findValueInEnum(formData.userRestriction, CouponUserTypeEnum)
          )
        }
      }
    });

    return result?.data?.couponCreate?.errors;
  };

  const onDraft = async (formData: CouponCreateFormData) => {
    setSelectedCollection(formData.couponCollection);
    const validateDraft = formDataValidations(formData);
    if (validateDraft) {
      notify({
        status: "error",
        text: `${validateDraft.text}`,
        title: `${validateDraft.title}`
      });
      return;
    }

    const result = await couponDraftCreate({
      variables: {
        first: 100,
        input: {
          accessImage: accessImageUpload,
          accessPageDescription: JSON.stringify(formData.accessPageDescription),
          accessPageSubtitle: formData.subtitle,
          accessPageTagline: formData.tagline,
          assignee: maybe(() =>
            findValueInEnum(formData.assignee, CouponAssigneeEnum)
          ),
          category: maybe(() =>
            findValueInEnum(formData.category, CouponCategoryEnum)
          ),
          code: formData.couponCode,
          dealType: maybe(() =>
            findValueInEnum(formData.couponClassification, CouponDealTypeEnum)
          ),
          discountValue: formData.couponValue || null,
          endDate: changeTimeZone(
            formData.couponEndDate,
            formData.couponEndTime
          ),
          eventEndDate: changeTimeZone(
            formData.eventStartDate,
            formData.eventStartTime
          ),
          eventLocation: formData.eventLocation,
          eventStartDate: changeTimeZone(
            formData.eventStartDate,
            formData.eventStartTime
          ),
          hasMalls: formData.hasMalls,
          isAppExclusive: formData.isAppExclusive,
          isExclusiveRedemption: formData.isExclusiveRedemption,
          isFeatured: formData.isFeatured,
          isHidden: formData.isHidden,
          isNationwide: formData.isNationwide,
          isUsageTimerApplied: formData.isUsageTimerApplied,
          mainPromoDescription: JSON.stringify(formData.mainPromoDescription),
          mainPromoTitle: formData.mainPromoTitle,
          malls: formData.malls,
          mechanics: JSON.stringify(formData.mechanics),
          name: formData.mainPromoTitle,
          occurence: maybe(() =>
            findValueInEnum(formData.occurrence, CouponOccurenceEnum)
          ),
          occurenceExclude: maybe(() => {
            const excludedDaysArray = Array.isArray(formData.excludedDays)
              ? formData.excludedDays
              : [formData.excludedDays];
            return excludedDaysArray.map(day =>
              findValueInEnum(day, CouponOccurenceExcludeEnum)
            );
          }),
          promoImage: promoImageUpload,
          quantity: formData.totalCopies || null,
          redeemEndDate: changeTimeZone(
            formData.redeemingEndDate,
            formData.redeemingEndTime
          ),
          redeemStartDate: changeTimeZone(
            formData.redeemingStartDate,
            formData.redeemingStartTime
          ),
          redeemType: maybe(() =>
            findValueInEnum(formData.redeemType, CouponRedeemTypeEnum)
          ),
          redemptionExclude: maybe(() => {
            const excludedDaysArray = Array.isArray(formData.excludedDays)
              ? formData.excludedDays
              : [formData.excludedDays];
            return excludedDaysArray.map(day =>
              findValueInEnum(day, CouponOccurenceExcludeEnum)
            );
          }),
          redemptionFrequency: maybe(() =>
            findValueInEnum(formData.occurrence, CouponOccurenceEnum)
          ),
          startDate: changeTimeZone(
            formData.couponStartDate,
            formData.couponStartTime
          ),
          usageLimit: resolveCouponUsageLimitForCreate(
            canManageCouponUsageLimitByDealType(
              userPermissions,
              formData.couponClassification
            ),
            formData.usageLimit
          ),
          usageTimerHour: formData.usageTimerHour,
          usageTimerMinute: formData.usageTimerMinute,
          usageTimerSecond: formData.usageTimerSecond,
          userType: maybe(() =>
            findValueInEnum(formData.userRestriction, CouponUserTypeEnum)
          )
        }
      }
    });

    if (result && result.data && result.data.draftCouponCreate) {
      return result.data.draftCouponCreate.errors;
    }

    return null;
  };

  return (
    <CouponCreatePage
      selectedCategory={selectedCategory}
      id={"CouponCreatePage"}
      userPermissions={userPermissions}
      onBack={() => navigate(couponManageUrl)}
      saveButtonBarState={couponCreateResult.status}
      onSubmit={onSubmit}
      onDraft={onDraft}
      disabled={couponCreateResult.loading || couponDraftCreateResult.loading}
      errors={couponCreateResult?.data?.couponCreate.errors || []}
      malls={mallTags}
      fetchMoreMalls={{
        hasMore: maybe(() => searchMallOpts.data.search.pageInfo.hasNextPage),
        loading: searchMallOpts.loading,
        onFetchMore: loadMoreMalls
      }}
      fetchMall={searchMall}
      promoImage={promoImageUpload}
      accessImage={accessImageUpload}
      onPromoImageUpload={setPromoImageUpload}
      onAccessImageUpload={setAccessImageUpload}
      fetchMoreCollection={{
        hasMore: searchCollectionOpts.data?.search?.pageInfo?.hasNextPage,
        loading: searchCollectionOpts.loading,
        onFetchMore: loadMoreCollection
      }}
      setSelectedCategory={setSelectedCategory}
      couponCollections={(searchCollectionOpts.data?.search?.edges || []).map(
        edge => edge.node
      )}
      fetchCollections={searchCollection}
    />
  );
};

export default CouponCreateView;
